import { Button, Table } from 'antd'
import type { TableColumnsType } from 'antd'
import React, { useEffect } from 'react'
import styled from 'styled-components'
import Scrollbars from 'react-custom-scrollbars'
import { IconFont } from '../iconfont'
import { ESDataType, ESSubDataType } from '@/pages/endpoint-security/task-list/task-list-main'
import { CSDataType, CSSubDataType } from '@/pages/container-security/task-list/task-list-main'
import { DSDataType, DSSubDataType } from '@/pages/data-protection/task-list/task-list-main'
import { TSDataType, TSSubDataType } from '@/pages/threat-protection/task-list/task-list-main'
import { t } from '@/i18n'

interface TaskTableProps {
  // 批量操作插槽
  batchOperation: React.ReactNode
  // 主任务表格列配置
  columns: TableColumnsType<ESDataType>
  // 子任务表格列配置
  subColumns: TableColumnsType<ESSubDataType>
  // 主任务数据
  dataSource: ESDataType[] | CSDataType[] | DSDataType | TSDataType[]
  // 子任务数据
  subDataSource: ESSubDataType[] | CSSubDataType[] | DSSubDataType[] | TSSubDataType[]
  // 搜索参数
  searchParams: Record<string, any>
  // 选中主任务行
  selectedRowKeys: string[]
  // 选中子任务行
  selectedSubRowKeys: string[]
  // 展开行
  expandedRowKeys: string[]
  // 主任务总条数
  total: number
  // 加载状态
  loading: boolean
  // 子任务加载状态
  subLoading: boolean
  // 是否有更多数据 (子任务表格)
  hasMore: boolean
  // 设置选中主任务行
  setSelectedRowKeys: (keys: string[]) => void
  // 设置选中子任务行
  setSelectedSubRowKeys: (keys: string[]) => void
  // 设置搜索参数
  setSearchParams: (params: Record<string, any>) => void
  // 展开行事件
  expandHandler: (params: { record: ESDataType; expanded: boolean }) => void
  // 加载更多子任务数据
  loadMoreSubData: (id: string, verificationType: string, mainTaskName: string) => void
  // 处理表格变化事件（筛选、排序、分页）
  onTableChange?: (pagination: any, filters: any, sorter: any) => void
  // 处理子表格变化事件（筛选、排序、分页）
  onSubTableChange?: (pagination: any, filters: any, sorter: any) => void
  // 表格变化时的加载状态
  tableChangeLoading?: boolean
}

const TaskTable: React.FC<TaskTableProps> = ({
  batchOperation,
  columns,
  subColumns,
  dataSource,
  subDataSource,
  searchParams,
  selectedRowKeys,
  selectedSubRowKeys,
  expandedRowKeys,
  total,
  loading,
  subLoading,
  hasMore,
  setSearchParams,
  setSelectedRowKeys,
  setSelectedSubRowKeys,
  expandHandler,
  loadMoreSubData,
  onTableChange,
  onSubTableChange,
  tableChangeLoading,
}) => {
  let whiteList: string[] = []

  const loadMore = () => {
    const tableItem = dataSource.find(item => item.id === expandedRowKeys[0])
    if (tableItem) {
      loadMoreSubData(tableItem.id, tableItem.verificationType, tableItem.name)
    }
  }

  const expandedRowRender = () => {
    const rowSelection = {
      selectedRowKeys: selectedSubRowKeys,
      renderCell: (
        checked: boolean,
        record: ESSubDataType,
        index: number,
        originNode: React.ReactNode,
      ) => {
        const { isRemove } = record
        if (isRemove) return originNode
        whiteList.push(record.id)
        return null
      },
      onSelect: (record: ESSubDataType) => {
        if (selectedSubRowKeys.includes(record.id)) {
          setSelectedSubRowKeys(selectedSubRowKeys.filter(key => key !== record.id))
        } else {
          setSelectedSubRowKeys([...selectedSubRowKeys, record.id])
        }
      },
      onSelectAll: (selected: boolean, selectedRows: ESSubDataType[]) => {
        let tempSelectedRows = selectedRows.filter(row => row !== undefined)
        const arr = tempSelectedRows.filter(row => !whiteList.includes(row.id)).map(row => row.id)
        setSelectedSubRowKeys(arr)
      },
    }

    return (
      <>
        <Table
          columns={subColumns}
          rowSelection={rowSelection}
          dataSource={subDataSource}
          loading={subLoading}
          pagination={false}
          showSorterTooltip={false}
          onChange={onSubTableChange}
          scroll={{ x: 1200 }}
        />

        {hasMore && !subLoading && (
          <div className="load-more-btn-wrapper">
            <Button className="load-more-btn" onClick={loadMore}>
              <span>{t('加载更多...')}</span>
            </Button>
          </div>
        )}
      </>
    )
  }

  // 展开行事件
  const handleExpand = (expanded: boolean, record: ESDataType) => {
    expandHandler({ record, expanded })
  }

  // 自定义展开图标
  const expandIcon = ({
    expanded,
    onExpand,
    record,
  }: {
    expanded: boolean
    onExpand: (record: ESDataType, expanded: boolean) => void
    record: ESDataType
  }) => {
    if (expanded)
      return (
        <IconFont
          type="icon-xiala-"
          className="retract-icon"
          onClick={() => onExpand(record, expanded)}
        />
      )

    return (
      <IconFont
        type="icon-xiala-"
        className="expand-icon"
        onClick={() => onExpand(record, expanded)}
      />
    )
  }

  // 展开行配置
  const expandable = {
    expandedRowKeys,
    expandedRowRender,
    expandIcon: expandIcon,
    onExpand: handleExpand,
  }

  // 主任务行选择配置
  const rowSelection = {
    selectedRowKeys,
    renderCell: (
      checked: boolean,
      record: ESDataType,
      index: number,
      originNode: React.ReactNode,
    ) => {
      const { isRemove } = record
      if (isRemove) return originNode
      return null
    },
    onSelect: (record: ESDataType) => {
      if (selectedRowKeys.includes(record.id)) {
        setSelectedRowKeys(selectedRowKeys.filter(key => key !== record.id))
        setSelectedSubRowKeys(selectedSubRowKeys.filter(key => !record?.subRowKeys?.includes(key)))
      } else {
        setSelectedRowKeys([...selectedRowKeys, record.id])
        setSelectedSubRowKeys([...selectedSubRowKeys, ...record?.subRowKeys])
      }
    },
    onSelectAll: (selected: boolean, selectedRows: ESDataType[]) => {
      setSelectedRowKeys(selectedRows.map(row => row.id))
    },
  }

  useEffect(
    () => () => {
      whiteList = []
    },
    [],
  )

  return (
    <Wrapper>
      {(selectedRowKeys.length > 0 || selectedSubRowKeys.length > 0) && (
        <div className="batch-operation">{batchOperation}</div>
      )}
      <Scrollbars autoHeight autoHeightMax={'100%'}>
        <Table
          rowKey="key"
          columns={columns}
          loading={loading || tableChangeLoading}
          expandable={expandable}
          rowSelection={rowSelection}
          dataSource={dataSource}
          showSorterTooltip={false}
          onChange={onTableChange}
          scroll={{ x: 1300 }}
          pagination={{
            total,
            showTotal: () => (
              <span>
                {t('共')}
                <span style={{ color: '#4238c9' }}>{total}</span>
                {t('条')}
              </span>
            ),
            pageSize: searchParams.limit || 10,
            current: searchParams.page || 1,
            showQuickJumper: true,
            showSizeChanger: true,
            pageSizeOptions: ['10', '20', '30', '50'],
            onChange: (page, pageSize) => {
              setSearchParams({ ...searchParams, page, limit: pageSize })
            },
          }}
        />
      </Scrollbars>
    </Wrapper>
  )
}

export default TaskTable

const Wrapper = styled.div`
  > div {
    width: 100%;
    height: 100%;
  }
  .batch-operation {
    width: 100%;
    height: 32px;
    background: #f1f3f8;
    border-radius: 6px;
    border: 1px solid #d3d5e6;
    padding-left: 20px;
    line-height: 30px;

    .batch-delete-btn {
      width: 92px;
      height: 24px;
      font-size: 14px;
      border-radius: 6px;
      display: flex;
      align-items: center;
      justify-content: center;

      &:hover {
        background-color: #fff;
        cursor: pointer;
      }

      .icon-delete {
        width: 12px;
        height: 12px;
        margin-right: 3px;
      }
    }

    .batch-report-btn {
      width: 120px;
      height: 24px;
      font-size: 14px;
      border-radius: 6px;
      display: flex;
      align-items: center;
      justify-content: center;

      &.disabled {
        color: #999;

        &:hover {
          background-color: #f5f5f5;
          cursor: not-allowed;
        }
      }

      &:hover {
        background-color: #fff;
        cursor: pointer;
      }

      .icon-export {
        width: 12px;
        height: 12px;
        margin-right: 3px;
      }
    }
  }

  .ant-table-filter-column {
    justify-content: flex-start;
    .ant-table-column-title {
      flex: none;
    }
  }
  .ant-table-expanded-row-level-1 td {
    &:has(.load-more-btn-wrapper) {
      padding-bottom: 0 !important;
    }
  }

  .ant-table {
    .ant-table {
      margin-right: -5px !important;
      margin: -16px -16px -16px 0;
      max-width: 100%;
    }
    table {
      max-width: max-content;
    }
  }
  .ant-table-tbody > tr > td > .ant-table-wrapper:only-child .ant-table,
  .ant-table-tbody
    > tr
    > td
    > .ant-table-expanded-row-fixed
    > .ant-table-wrapper:only-child
    .ant-table {
    margin: -16px -16px -16px 0;
  }

  .ant-table .ant-table-container .ant-table-container {
    margin-left: 40px;
    .ant-table-selection-column {
      text-align: left;
      width: 40px;
      z-index: 1;
    }
  }

  // 确保tooltip的z-index高于选择列
  .ant-tooltip {
    z-index: 1000 !important;
  }

  .ant-table-expanded-row {
    .ant-table-cell {
      .sub-table-wrapper {
        padding-left: 40px !important;
        width: 100%;
      }
    }
    .ant-table-cell-row-hover {
      background-color: #f4f4f5 !important;
    }
    thead th {
      color: #333;
    }
    tr {
      padding: 6px !important;
      background-color: #fff !important;
    }
    td {
      background-color: #fff !important;
    }
    .ant-table-tbody > tr > td {
      background-color: #f8f9fb !important;
      color: #444;
    }
    .ant-table-thead tr th {
      background-color: #f1f3f8 !important;
    }
    .ant-table-tbody > tr > td.ant-table-cell-row-hover {
      background-color: #f4f4f5 !important;
    }
  }

  .retract-icon {
    color: #666;
    transform: rotate(180deg);

    &:hover {
      cursor: pointer;
    }
  }

  .expand-icon {
    color: #666;

    &:hover {
      cursor: pointer;
    }
  }

  .score {
    display: inline-block;
    max-width: 45px;
    flex-shrink: 0;
  }

  .divider {
    margin: 0 8px;
  }

  .low-risk {
    color: #67c23a;
  }

  .medium-risk {
    color: #74a1fa;
  }

  .high-risk {
    color: #ffa70b;
  }

  .weaker-risk {
    color: #ff2f56;
  }

  .serious-risk {
    color: #b70001;
  }

  .status {
    min-width: 54px;
    height: 18px;
    border-radius: 4px;
    margin-right: 4px;
    line-height: 18px;
    text-align: center;
    font-size: 12px;

    .count {
      margin-left: 3px;
    }
  }

  .success {
    background: #e4f7d1;
    color: #67c23a;
  }

  .processing {
    background: #dbe8ff;
    color: #2973ff;
  }

  .waiting {
    background: #d9d9d9;
    color: #666;
  }

  .error {
    min-width: 42px;
    background: #ffd8d8;
    color: #ff2929;
  }

  .warning {
    background: #fcefd6;
    color: #f29900;
  }

  .kind-wrapper {
    max-width: 100px;
    height: 18px;
    background: #e3e1f7;
    border-radius: 4px;
    display: flex;
    font-size: 12px;
    line-height: 18px;

    &.kind-immediately {
      max-width: 33px;
    }

    .kind {
      min-width: 33px;
      height: 18px;
      background: #d4d1f4;
      border-radius: 4px;
      color: #4238c9;
      text-align: center;
      margin-right: 4px;
    }

    .divider {
      margin: 0 6px;
    }
  }

  .download-btn-disabled,
  .report-btn-disabled,
  .remove-btn-disabled {
    color: #ccc;
    flex-shrink: 0;

    &:hover {
      cursor: not-allowed;
    }

    .text {
      margin-left: 6px;
    }
  }

  .stop-btn,
  .view-btn,
  .copy-btn,
  .download-btn,
  .remove-btn,
  .report-btn {
    min-width: 48px;
    color: #4238c9;
    flex-shrink: 0;

    &:hover {
      cursor: pointer;
    }

    .text {
      margin-left: 6px;
    }
  }

  .ant-dropdown-trigger {
    flex-shrink: 0;
    color: #4238c9;
  }

  .view-btn-disabled {
    min-width: 48px;
    color: #ccc;
    flex-shrink: 0;

    &:hover {
      cursor: not-allowed;
    }

    .text {
      margin-left: 6px;
    }
  }

  .ant-dropdown {
    width: 105px;
  }

  .ant-table-filter-column {
    .ant-dropdown {
      width: auto;
    }
  }

  .ant-table-cell-fix-right {
    .ant-dropdown-trigger {
      color: #4238c9;
    }
  }
  .status-wrapper {
    display: flex;

    .plugin-status {
      /* min-width: 60px; */
      flex-shrink: 0;
    }

    .status {
      height: 18px;
      border-radius: 4px;
      text-align: center;
      line-height: 18px;
      font-size: 12px;
      margin-left: 8px;
    }

    .success {
      min-width: 44px;
      background: #e4f7d1;
      color: #67c23a;
    }

    .processing {
      min-width: 44px;
      background: #dbe8ff;
      color: #2973ff;
    }

    .waiting {
      min-width: 44px;
      background: #d9d9d9;
      color: #666;
    }

    .error {
      min-width: 32px;
      background: #ffd8d8;
      color: #ff2929;
    }

    .warning {
      min-width: 44px;
      background: #fcefd6;
      color: #f29900;
    }
  }

  .agent-wrapper {
    display: flex;
    align-items: center;

    .icons {
      display: flex;
      flex-direction: column;
      align-items: center;
      margin-right: 6px;

      .attack-icon {
        width: 14px;
        height: 14px;
      }

      .arrow-icon {
        transform: rotate(90deg);
      }
    }

    .agent {
      display: flex;
      flex-direction: column;
      justify-content: space-between;
      min-height: 42px;

      .attack-agent {
        width: 120px;
        line-height: 14px;
      }

      .defense-agent {
        width: 120px;
        line-height: 14px;
      }
    }
  }

  .sub-table-wrapper {
    display: flex;
    flex-direction: column;
    align-items: center;

    .ant-table-cell {
      flex-shrink: 0;
    }
  }

  .load-more-btn-wrapper {
    width: 100%;
    display: flex;
    justify-content: center;
    align-items: center;
    margin-top: 16px;
  }

  .load-more-btn {
    width: 144px; /* 梯形的宽度 */
    height: 18px; /* 梯形的高度 */
    background-color: #e7e9f3; /* 背景颜色 */
    clip-path: polygon(0 0, 35px 100%, calc(100% - 35px) 100%, 100% 0); /* 梯形的剪切路径 */
    color: #4238c9; /* 文字颜色 */
    display: flex; /* 使文本居中 */
    align-items: center; /* 垂直居中 */
    justify-content: center; /* 水平居中 */
    cursor: pointer; /* 鼠标样式 */
    font-size: 12px; /* 字体大小 */
    margin-top: -4px;
    border: none;
  }

  .ant-table-column-sorter {
    display: flex;
    justify-content: center;
    align-items: center;
  }

  .ant-table-column-sorters {
    justify-content: flex-start;

    .ant-table-column-title {
      flex: none;
    }
  }

  .ant-table-filter-column {
    justify-content: flex-start;

    .ant-table-column-title {
      flex: unset;
    }

    .ant-table-filter-trigger:hover {
      background: transparent;
    }
  }

  .ant-table-measure-row {
    display: none;
  }
`
